import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    X, Loader2, AlertCircle, CheckCircle2, FileText, Settings2, FileBarChart, Landmark, Banknote, Smartphone,
    CreditCard, Wallet, Lock, AlertTriangle, ArrowDownLeft, ArrowUpRight, Receipt, Clock, MessageCircle,
    Copy, Check, ChevronLeft, ChevronDown, UserRound, Link2, Truck, Send, XCircle,
} from 'lucide-react';
import { useStore } from '../../../context/StoreContext';
import { useAuth } from '../../../context/AuthContext';
import { SMIIFY_API } from '../../../config/api';
import { inr, todayYmd, longDate } from '../../../utils/money';
import { buildClosingMessage, waUrl, prettyPhone, isValidWaNumber, toWaNumber } from '../../../utils/closingMessage';
import styles from './ClosureReportModal.module.css';

// Default Utility template for the day closing (see the setup guide). Any approved Utility template can be chosen.
const DEFAULT_TEMPLATE = 'daily_closing_report';
const templateKey = (storeId) => `hirsta.closingTemplate.${storeId}`;

/**
 * Day closing (EOD)
 * Today's real numbers -> sent to the owner numbers:
 *   - on the WhatsApp API (the branch's WhatsApp line + an approved Utility template): one tap, sent from the server
 *   - or from your own WhatsApp (wa.me) with a view-only closing page link
 */
const ClosureReportModal = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const { hasPermission } = useAuth();
    const storeId = activeStore?._id || activeStore?.id;
    const isHQ = !storeId || storeId === 'global_hq' || activeStore?.isHQ === true;
    const storeName = activeStore?.storeName || activeStore?.name || 'Branch';
    const date = todayYmd();
    const canSeeDrawer = hasPermission('cashflow', 'view');
    const canSeeReport = hasPermission('reports', 'view_daily_report') || hasPermission('reports', 'view_eod_report');

    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState('');
    const [recipients, setRecipients] = useState([]);       // [{ phone, name }]
    const [link, setLink] = useState('');
    const [linkState, setLinkState] = useState('idle');      // idle | loading | ready | failed
    const [view, setView] = useState('summary');             // summary | send
    const [sentTo, setSentTo] = useState([]);
    const [otherNumber, setOtherNumber] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [copied, setCopied] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    // WhatsApp API (the branch's line)
    const [apiTemplates, setApiTemplates] = useState(null);  // null = checking, [] = none usable
    const [apiTemplate, setApiTemplate] = useState('');
    const [apiSending, setApiSending] = useState(false);
    const [apiResult, setApiResult] = useState(null);        // { ok, message, results: [{ phone, ok, error }] }
    const [settingsTpl, setSettingsTpl] = useState(null);    // { name, lang } chosen in the day closing settings
    const [userPicked, setUserPicked] = useState(false);

    const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId }), [storeId]);

    // reset every time the modal opens
    useEffect(() => { if (isOpen) { setView('summary'); setSentTo([]); setOtherNumber(''); setShowPreview(false); setApiResult(null); } }, [isOpen]);

    // 1) today's numbers + owner numbers from Config
    useEffect(() => {
        if (!isOpen || isHQ) return undefined;
        let alive = true;
        (async () => {
            setLoading(true); setError('');
            try {
                const [repRes, waRes] = await Promise.all([
                    fetch(`${SMIIFY_API}/daily-report?date=${date}`, { headers }),
                    fetch(`${SMIIFY_API}/integrations/whatsapp?storeId=${storeId}`, { headers }).catch(() => null),
                ]);
                const rep = await repRes.json().catch(() => ({}));
                const wa = waRes ? await waRes.json().catch(() => ({})) : {};
                if (!alive) return;
                if (!repRes.ok || !rep.success) throw new Error(rep.message || `Could not load today's numbers (error ${repRes.status}).`);
                setReport(rep.data);
                // the settings that really apply: this branch's own, or HQ's
                const eod = wa?.data?.effectiveEod || wa?.data?.eodAutomation || {};
                setSettingsTpl(eod.templateName ? { name: eod.templateName, lang: eod.templateLang || '' } : null);
                const seen = new Set();
                setRecipients((eod.recipients || [])
                    .filter((r) => r.isActive && isValidWaNumber(r.phone))
                    .filter((r) => { const k = toWaNumber(r.phone); if (seen.has(k)) return false; seen.add(k); return true; })
                    .map((r) => ({ phone: r.phone, name: r.name || r.label || '' })));
            } catch (e) {
                if (alive) { setReport(null); setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); }
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [isOpen, isHQ, storeId, date, headers, reloadKey]);

    // 1b) approved Utility templates on the branch's WhatsApp line (no line = API sending is not offered)
    useEffect(() => {
        if (!isOpen || isHQ) return undefined;
        let alive = true;
        setApiTemplates(null);
        fetch(`${SMIIFY_API}/templates`, { headers })
            .then((res) => res.json().then((j) => ({ ok: res.ok, j })))
            .then(({ ok, j }) => {
                if (!alive) return;
                const list = ok && j.success && Array.isArray(j.data)
                    ? j.data.filter((t) => t.status === 'APPROVED' && String(t.category).toUpperCase() === 'UTILITY')
                    : [];
                setApiTemplates(list);
            })
            .catch(() => alive && setApiTemplates([]));
        return () => { alive = false; };
    }, [isOpen, isHQ, storeId, headers]);

    // template to start with: the one in the day closing settings, else the last one used here, else the default
    useEffect(() => {
        if (!apiTemplates || userPicked) return;
        const saved = localStorage.getItem(templateKey(storeId));
        const pick = (settingsTpl && (apiTemplates.find((t) => t.name === settingsTpl.name && t.lang === settingsTpl.lang) || apiTemplates.find((t) => t.name === settingsTpl.name)))
            || apiTemplates.find((t) => t.name === saved) || apiTemplates.find((t) => t.name === DEFAULT_TEMPLATE) || apiTemplates[0];
        setApiTemplate(pick ? `${pick.name}|${pick.lang}` : '');
    }, [apiTemplates, settingsTpl, userPicked, storeId]);
    useEffect(() => { if (isOpen) setUserPicked(false); }, [isOpen]);

    // 2) the view-only closing page link, made in advance so WhatsApp opens instantly on click
    useEffect(() => {
        if (!report || !storeId) return undefined;
        let alive = true;
        setLinkState('loading');
        fetch(`${SMIIFY_API}/daily-report/share`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ date }) })
            .then((res) => res.json().then((j) => ({ ok: res.ok, j })))
            .then(({ ok, j }) => { if (!alive) return; if (ok && j.success && j.data?.url) { setLink(j.data.url); setLinkState('ready'); } else setLinkState('failed'); })
            .catch(() => alive && setLinkState('failed'));
        return () => { alive = false; };
    }, [report, storeId, date, headers]);

    const message = useMemo(() => (report ? buildClosingMessage({ report, storeName, link }) : ''), [report, storeName, link]);

    if (!isOpen) return null;

    const t = report?.totals; const p = report?.payments; const d = report?.drawer; const r = report?.dayResult; const ex = report?.expenses;
    const diff = d?.closing?.difference;
    const preparing = linkState === 'loading';
    const apiReady = Boolean(apiTemplates && apiTemplates.length && apiTemplate);
    const canApiSend = apiReady && recipients.length > 0;
    const chosen = (apiTemplates || []).find((x) => `${x.name}|${x.lang}` === apiTemplate);

    // must run straight from the click, or the browser blocks the new tab
    const openWhatsApp = (phone) => {
        window.open(waUrl(phone, message), '_blank', 'noopener');
        if (phone) setSentTo((list) => (list.includes(toWaNumber(phone)) ? list : [...list, toWaNumber(phone)]));
    };
    const onSendOwn = () => {
        if (recipients.length === 1) openWhatsApp(recipients[0].phone);   // one owner number: straight to that chat
        else setView('send');                                             // several or none: choose
    };

    // one tap: the server sends the template to every owner number
    const sendWithApi = async () => {
        if (!canApiSend || apiSending) return;
        setApiSending(true); setApiResult(null);
        try {
            localStorage.setItem(templateKey(storeId), chosen?.name || '');
            const res = await fetch(`${SMIIFY_API}/daily-report/send-whatsapp`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ date, templateName: chosen?.name, language: chosen?.lang, phones: recipients.map((x) => x.phone) }),
            });
            const j = await res.json().catch(() => ({}));
            setApiResult({ ok: Boolean(j.success), message: j.message || (res.ok ? 'Sent.' : `Could not send (error ${res.status}).`), results: j.data?.results || [] });
        } catch (e) {
            setApiResult({ ok: false, message: e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message, results: [] });
        } finally { setApiSending(false); }
    };

    const copyMessage = async () => {
        try { await navigator.clipboard.writeText(message); } catch (e) { window.prompt('Copy the message', message); }
        setCopied(true); setTimeout(() => setCopied(false), 1800);
    };
    const go = (path) => { onClose(); navigate(path); };
    const nameOf = (phone) => recipients.find((x) => toWaNumber(x.phone) === toWaNumber(phone))?.name || prettyPhone(phone);

    return (
        <div className={styles.overlay} role="presentation" onClick={onClose}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Day closing" onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        {view === 'send'
                            ? <button type="button" className={styles.backBtn} onClick={() => setView('summary')} aria-label="Back"><ChevronLeft size={18} /></button>
                            : <span className={styles.iconWrap}><FileText size={18} strokeWidth={2.4} /></span>}
                        <div>
                            <h3>{view === 'send' ? 'Send from your WhatsApp' : 'Day closing'}</h3>
                            <p>{storeName} · {longDate(date)}</p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className={styles.closeBtn} aria-label="Close"><X size={18} /></button>
                </div>

                <div className={styles.body}>
                    {error && (
                        <div className={`${styles.toast} ${styles.toastError}`}>
                            <AlertCircle size={15} /><span>{error}</span>
                            <button type="button" className={styles.retry} onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
                        </div>
                    )}

                    {isHQ ? (
                        <div className={styles.state}><AlertCircle size={20} /><strong>Select a branch</strong><span>The day closing is done for one branch at a time.</span></div>
                    ) : loading ? (
                        <div className={styles.state}><Loader2 size={22} className={styles.spin} /><span>Adding up today…</span></div>
                    ) : !report ? null : view === 'summary' ? (
                        <>
                            {/* ---------- today's result ---------- */}
                            <div className={styles.result}>
                                <div className={styles.resIn}>
                                    <span><ArrowDownLeft size={13} /> Money in</span>
                                    <strong>{inr(r.moneyIn)}</strong>
                                    <small>{t.bills} bills · {t.customersServed} clients</small>
                                </div>
                                <div className={styles.resOut}>
                                    <span><ArrowUpRight size={13} /> Expenses</span>
                                    <strong>{inr(r.expensesTotal)}</strong>
                                    <small>Cash {inr(r.expensesCash)} · Online {inr(r.expensesOnline)}</small>
                                </div>
                                <div className={`${styles.resNet} ${r.net < 0 ? styles.resLoss : ''}`}>
                                    <span>{r.net < 0 ? 'Spent more than earned' : 'Left today'}</span>
                                    <strong>{inr(r.net)}</strong>
                                    <small>Money in − expenses</small>
                                </div>
                            </div>

                            <div className={styles.sectionTitle}>How money came in</div>
                            <div className={styles.modes}>
                                <div><Banknote size={13} /><span>Cash</span><b>{inr(p.cash)}</b></div>
                                <div><Smartphone size={13} /><span>UPI</span><b>{inr(p.upi)}</b></div>
                                <div><CreditCard size={13} /><span>Card</span><b>{inr(p.card)}</b></div>
                                <div><Wallet size={13} /><span>Khata + prepaid used</span><b>{inr(p.advanceUsed)}</b></div>
                                <div className={p.dueCreated > 0 ? styles.modeRed : ''}><AlertTriangle size={13} /><span>Udhaar given</span><b>{inr(p.dueCreated)}</b></div>
                                <div><ArrowDownLeft size={13} /><span>Old udhaar / advance</span><b>{inr(report.outside?.total || 0)}</b></div>
                            </div>

                            <div className={styles.sectionTitle}>Where money went</div>
                            <div className={styles.expenses}>
                                <div className={styles.expRow}><span><Receipt size={13} /> Expenses paid in cash</span><b className={styles.neg}>{inr(ex?.cash || 0)}</b></div>
                                <div className={styles.expRow}><span><Smartphone size={13} /> Expenses paid online</span><b>{inr(ex?.nonCash || 0)}</b></div>
                                {d.pettyExpense > 0 && <div className={styles.expRow}><span><Banknote size={13} /> Petty cash</span><b className={styles.neg}>{inr(d.pettyExpense)}</b></div>}
                                {d.bankDeposit > 0 && <div className={styles.expRow}><span><Landmark size={13} /> Deposited in bank</span><b>{inr(d.bankDeposit)}</b></div>}
                                {d.ownerWithdrawal > 0 && <div className={styles.expRow}><span><Wallet size={13} /> Owner took cash</span><b>{inr(d.ownerWithdrawal)}</b></div>}
                                {ex?.vendor?.paid > 0 && <div className={styles.expRow}><span><Truck size={13} /> Paid to vendors{ex.vendor.notCleared > 0 ? ` (${inr(ex.vendor.notCleared)} not cleared)` : ''}</span><b>{inr(ex.vendor.paid)}</b></div>}
                                {report.vendorPurchases?.count > 0 && (
                                    <div className={styles.expRow}>
                                        <span><Truck size={13} /> Bought from vendors ({report.vendorPurchases.count} {report.vendorPurchases.count === 1 ? 'bill' : 'bills'})</span>
                                        <b>{inr(report.vendorPurchases.total)}{report.vendorPurchases.toPay > 0 && <small className={styles.neg} style={{ marginLeft: 6, fontWeight: 800 }}>{inr(report.vendorPurchases.toPay)} to pay</small>}</b>
                                    </div>
                                )}
                                {(ex?.byCategory || []).length > 0 && <div className={styles.cats}>{ex.byCategory.slice(0, 4).map((c) => <span key={c.name}>{c.name} <b>{inr(c.amount)}</b></span>)}</div>}
                                {ex?.pendingCount > 0 && <p className={styles.pending}><Clock size={12} /> {ex.pendingCount} {ex.pendingCount === 1 ? 'expense waits' : 'expenses wait'} for approval ({inr(ex.pending)})</p>}
                            </div>

                            <div className={styles.drawer}>
                                <div>
                                    <span><Landmark size={13} /> Cash that should be in the drawer</span>
                                    <strong>{inr(d.expected)}</strong>
                                    <small>{d.closing ? `Counted ${inr(d.closing.countedCash)} · ${Math.abs(diff) < 0.01 ? 'matches' : `${diff < 0 ? 'short' : 'extra'} ${inr(Math.abs(diff))}`}` : 'Not counted yet. Count the drawer before you leave.'}</small>
                                </div>
                                {canSeeDrawer && <button type="button" className={styles.smallBtn} onClick={() => go('/cash-flow')}><Lock size={13} /> {d.closing ? 'View drawer' : 'Count and close'}</button>}
                            </div>

                            {/* ---------- WhatsApp API: one tap to every owner number ---------- */}
                            {apiReady ? (
                                <div className={`${styles.alertBox} ${styles.alertOn}`} style={{ display: 'block' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                        <div>
                                            <strong>Send with WhatsApp API</strong>
                                            <span>
                                                {recipients.length
                                                    ? `Goes straight to ${recipients.length === 1 ? (recipients[0].name || prettyPhone(recipients[0].phone)) : `${recipients.length} owner numbers`}, with a button to the full closing page.`
                                                    : 'Add an owner number in Config to send with one tap.'}
                                            </span>
                                        </div>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                                            <span>Template</span>
                                            <select
                                                value={apiTemplate}
                                                onChange={(e) => { setApiTemplate(e.target.value); setUserPicked(true); setApiResult(null); }}
                                                style={{ height: 30, borderRadius: 8, border: '1px solid #e1dcf7', padding: '0 8px', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, maxWidth: 220 }}
                                            >
                                                {apiTemplates.map((tp) => <option key={`${tp.name}|${tp.lang}`} value={`${tp.name}|${tp.lang}`}>{tp.name}{tp.lang ? ` (${tp.lang})` : ''}{settingsTpl && settingsTpl.name === tp.name && (!settingsTpl.lang || settingsTpl.lang === tp.lang) ? ' · in settings' : ''}</option>)}
                                            </select>
                                        </label>
                                    </div>
                                    {apiResult && (
                                        <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
                                            <div className={`${styles.toast} ${apiResult.ok ? '' : styles.toastError}`} style={{ margin: 0 }}>
                                                {apiResult.ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}<span>{apiResult.message}</span>
                                            </div>
                                            {apiResult.results.length > 1 && apiResult.results.map((x) => (
                                                <div key={x.phone} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                                                    {x.ok ? <CheckCircle2 size={13} color="#007a49" /> : <XCircle size={13} color="#be1025" />}
                                                    <span>{nameOf(x.phone)}</span>
                                                    {!x.ok && <span style={{ color: '#be1025', fontWeight: 600 }}>{x.error}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={`${styles.alertBox} ${recipients.length ? styles.alertOn : styles.alertOff}`}>
                                    <div>
                                        <strong>
                                            {recipients.length === 1 ? `Opens WhatsApp to ${recipients[0].name || prettyPhone(recipients[0].phone)}`
                                                : recipients.length > 1 ? `${recipients.length} owner numbers saved`
                                                    : 'No owner number saved'}
                                        </strong>
                                        <span>
                                            {apiTemplates === null ? 'Checking your WhatsApp line…'
                                                : recipients.length ? 'The message has today\'s totals and a link to the full closing page.'
                                                    : 'You can still type a number or choose a chat in WhatsApp.'}
                                            {apiTemplates && !apiTemplates.length && ' To send with one tap from your WhatsApp line, add an approved Utility template for the day closing.'}
                                        </span>
                                    </div>
                                    <button type="button" onClick={() => go('/config/eod')} className={styles.smallBtn}><Settings2 size={13} /> Config</button>
                                </div>
                            )}
                        </>
                    ) : (
                        /* ---------- choose who to send to (own WhatsApp) ---------- */
                        <>
                            {recipients.length > 0 && (
                                <>
                                    <div className={styles.sectionTitle}>Owner numbers</div>
                                    <div className={styles.people}>
                                        {recipients.map((rc) => {
                                            const done = sentTo.includes(toWaNumber(rc.phone));
                                            return (
                                                <div key={rc.phone} className={`${styles.person} ${done ? styles.personDone : ''}`}>
                                                    <span className={styles.avatar}>{done ? <Check size={15} strokeWidth={3} /> : <UserRound size={15} />}</span>
                                                    <span className={styles.personText}>
                                                        <strong>{rc.name || prettyPhone(rc.phone)}</strong>
                                                        <small>{rc.name ? prettyPhone(rc.phone) : done ? 'Opened in WhatsApp' : 'Owner'}</small>
                                                    </span>
                                                    <button type="button" className={done ? styles.waGhost : styles.waBtn} onClick={() => openWhatsApp(rc.phone)} disabled={preparing}>
                                                        <MessageCircle size={14} /> {done ? 'Send again' : 'Send'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            <div className={styles.sectionTitle}>Another number</div>
                            <div className={styles.otherRow}>
                                <span className={styles.prefix}>+91</span>
                                <input
                                    value={otherNumber}
                                    onChange={(e) => setOtherNumber(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                                    inputMode="numeric"
                                    placeholder="10-digit mobile number"
                                    aria-label="Mobile number"
                                />
                                <button type="button" className={styles.waBtn} onClick={() => openWhatsApp(otherNumber)} disabled={otherNumber.length !== 10 || preparing}>
                                    <MessageCircle size={14} /> Send
                                </button>
                            </div>
                            <button type="button" className={styles.chooseBtn} onClick={() => openWhatsApp('')} disabled={preparing}>
                                <MessageCircle size={14} /> Choose a chat in WhatsApp
                            </button>
                        </>
                    )}

                    {/* message preview (your own WhatsApp), both views */}
                    {report && !isHQ && !loading && (
                        <div className={styles.preview}>
                            <button type="button" className={styles.previewHead} onClick={() => setShowPreview((v) => !v)} aria-expanded={showPreview}>
                                <span><MessageCircle size={13} /> Message preview</span>
                                <span className={styles.linkTag}>
                                    {linkState === 'ready' ? <><Link2 size={11} /> Closing link added</> : preparing ? <><Loader2 size={11} className={styles.spin} /> Adding link…</> : linkState === 'failed' ? 'Without link' : ''}
                                </span>
                                <ChevronDown size={15} className={showPreview ? styles.chevOpen : ''} />
                            </button>
                            {showPreview && (
                                <>
                                    <pre className={styles.previewText}>{message}</pre>
                                    <button type="button" className={styles.copyBtn} onClick={copyMessage}>{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy message'}</button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    {canSeeReport ? <button type="button" onClick={() => go('/reports/daily')} className={styles.linkBtn} disabled={isHQ}><FileBarChart size={14} /> Full report</button> : <span />}
                    <div className={styles.footerRight}>
                        {view === 'summary' && canApiSend && (
                            <button type="button" onClick={onSendOwn} disabled={loading || !report || preparing} className={styles.cancelBtn}>
                                <MessageCircle size={14} /> From my WhatsApp
                            </button>
                        )}
                        {!(view === 'summary' && canApiSend) && <button type="button" onClick={onClose} className={styles.cancelBtn}>Close</button>}
                        {view === 'summary' && canApiSend && (
                            <button type="button" onClick={sendWithApi} disabled={loading || !report || apiSending} className={styles.sendBtn}>
                                {apiSending ? <Loader2 size={15} className={styles.spin} /> : apiResult?.ok ? <CheckCircle2 size={15} /> : <Send size={15} />}
                                {apiSending ? 'Sending…' : apiResult?.ok ? 'Send again' : recipients.length === 1 ? 'Send now' : `Send to ${recipients.length}`}
                            </button>
                        )}
                        {view === 'summary' && !canApiSend && (
                            <button type="button" onClick={onSendOwn} disabled={loading || !report || preparing} className={styles.sendBtn}>
                                {preparing ? <Loader2 size={15} className={styles.spin} /> : <MessageCircle size={15} />}
                                {preparing ? 'Preparing…' : recipients.length === 1 ? 'Send on WhatsApp' : 'Send on WhatsApp…'}
                            </button>
                        )}
                        {view === 'send' && sentTo.length > 0 && (
                            <button type="button" onClick={onClose} className={styles.doneBtn}><CheckCircle2 size={15} /> Done</button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClosureReportModal;
